import AccountServer from '~/services/AccountServer'
import type {
  EnterpriseBalanceAlertConfig,
  EnterpriseBalanceAlertPool,
  EnterpriseBalanceAlertPoolSummary,
  EnterpriseBalanceAlertResponse,
} from '~/services/AccountServer/types'
import { NetCode } from '~/types/const'
import useUserStore from '~/store/modules/user'
import { getEnterpriseManagementAccess } from '~/utils/enterpriseManagement'
import {
  createDefaultEnterpriseBalanceAlertConfig,
  normalizeEnterpriseBalanceAlertBoolean,
  normalizeEnterpriseBalanceAlertConfig,
  normalizeEnterpriseBalanceAlertNumber,
  normalizeEnterpriseBalanceAlertPool,
  normalizeEnterpriseBalanceAlertStatus,
  reconcileLocalEnterpriseBalanceAlert,
  sortEnterpriseBalanceAlerts,
  shouldShowEnterpriseBalanceAlert,
} from '~/utils/enterpriseBalanceAlert'

const REFRESH_GAP_MS = 3000

export function useEnterpriseBalanceAlert() {
  const state = useState<EnterpriseBalanceAlertResponse | null>('enterprise-balance-alert-state', () => null)
  const loading = useState<boolean>('enterprise-balance-alert-loading', () => false)
  const dialogVisible = useState<boolean>('enterprise-balance-alert-dialog-visible', () => false)
  const lastLoadAt = useState<number>('enterprise-balance-alert-last-load-at', () => 0)
  const userStore = useUserStore()

  const fallbackConfig = computed(() => createDefaultEnterpriseBalanceAlertConfig())
  const config = computed(() => normalizeEnterpriseBalanceAlertConfig(state.value?.config || fallbackConfig.value))
  const canManage = computed(() => {
    const access = getEnterpriseManagementAccess(
      userStore.bizTeamInfo,
      userStore.userInfo?.bizTeamId,
      userStore.userInfo?.bizTeamMemberRole,
    )
    return Boolean(state.value?.canManage ?? access.isManager)
  })
  const planMode = computed(() => state.value?.planMode || getEnterpriseManagementAccess(
    userStore.bizTeamInfo,
    userStore.userInfo?.bizTeamId,
    userStore.userInfo?.bizTeamMemberRole,
  ).planMode)
  const poolOptions = computed(() => {
    const pools = (state.value?.pools || []).map(item => ({
      pool: normalizeEnterpriseBalanceAlertPool(item.pool),
      label: item.label || getDefaultPoolLabel(normalizeEnterpriseBalanceAlertPool(item.pool)),
      domain: item.domain || getDefaultPoolDomain(normalizeEnterpriseBalanceAlertPool(item.pool)),
      totalWords: Number(item.totalWords || 0),
      remainingWords: Number(item.remainingWords || 0),
    }))
    if (planMode.value === 'global') {
      const defaults = [
        { pool: 'VIP' as const, label: '中国池', domain: 'doclingo.vip', totalWords: 0, remainingWords: 0 },
        { pool: 'AI' as const, label: '国际池', domain: 'doclingo.ai', totalWords: 0, remainingWords: 0 },
      ]
      return defaults.map(item => pools.find(pool => pool.pool === item.pool) || item)
    }
    if (pools.length) return pools
    return [
      { pool: 'LOCAL' as const, label: '当前空间', domain: '', totalWords: 0, remainingWords: 0 },
    ]
  })
  const visibleAlerts = computed(() => {
    if (!canManage.value) return []
    const currentPool = normalizeEnterpriseBalanceAlertPool(state.value?.currentPool, String(useRuntimeConfig().public.siteEdition || '').toLowerCase() === 'vip' ? 'VIP' : 'AI')
    return sortEnterpriseBalanceAlerts(
      (state.value?.alerts || []).filter(item => shouldShowEnterpriseBalanceAlert(item, config.value)),
      currentPool,
    )
  })

  function getDefaultPoolLabel(pool: EnterpriseBalanceAlertPool) {
    if (pool === 'VIP') return '中国池'
    if (pool === 'AI') return '国际池'
    return '当前空间'
  }

  function getDefaultPoolDomain(pool: EnterpriseBalanceAlertPool) {
    if (pool === 'VIP') return 'doclingo.vip'
    if (pool === 'AI') return 'doclingo.ai'
    return ''
  }

  function replaceState(next?: EnterpriseBalanceAlertResponse | null) {
    const reconciled = next ? reconcileLocalEnterpriseBalanceAlert(next, userStore.bizTeamInfo) : null
    state.value = reconciled ? {
      ...reconciled,
      config: normalizeEnterpriseBalanceAlertConfig(reconciled.config),
      currentPool: normalizeEnterpriseBalanceAlertPool(reconciled.currentPool, String(useRuntimeConfig().public.siteEdition || '').toLowerCase() === 'vip' ? 'VIP' : 'AI'),
      pools: (reconciled.pools || []).map(item => ({
        ...item,
        pool: normalizeEnterpriseBalanceAlertPool(item.pool),
        totalWords: normalizeEnterpriseBalanceAlertNumber(item.totalWords),
        remainingWords: normalizeEnterpriseBalanceAlertNumber(item.remainingWords),
      })),
      alerts: (reconciled.alerts || []).map(item => ({
        ...item,
        pool: normalizeEnterpriseBalanceAlertPool(item.pool),
        status: normalizeEnterpriseBalanceAlertStatus(item.status),
        cycleId: String(item.cycleId || ''),
        totalWords: normalizeEnterpriseBalanceAlertNumber(item.totalWords),
        remainingWords: normalizeEnterpriseBalanceAlertNumber(item.remainingWords),
        thresholdChars: normalizeEnterpriseBalanceAlertNumber(item.thresholdChars),
        thresholdPercent: item.thresholdPercent === null || item.thresholdPercent === undefined
          ? null
          : normalizeEnterpriseBalanceAlertNumber(item.thresholdPercent),
        dismissible: normalizeEnterpriseBalanceAlertBoolean(item.dismissible, normalizeEnterpriseBalanceAlertStatus(item.status) !== 'EXHAUSTED'),
        dismissed: normalizeEnterpriseBalanceAlertBoolean(item.dismissed, false),
      })),
    } : null
  }

  async function refresh(force = false) {
    if (process.server || loading.value) return
    const access = getEnterpriseManagementAccess(
      userStore.bizTeamInfo,
      userStore.userInfo?.bizTeamId,
      userStore.userInfo?.bizTeamMemberRole,
    )
    if (!userStore.token || !access.hasEnterprise || !access.hasActivePlan || !access.isManager) {
      replaceState(null)
      return
    }
    const now = Date.now()
    if (!force && now - lastLoadAt.value < REFRESH_GAP_MS) return

    loading.value = true
    lastLoadAt.value = now
    try {
      const response = await AccountServer.getEnterpriseBalanceAlert()
      if (response.code === NetCode.SUCCESS && response.data) replaceState(response.data)
      else replaceState(null)
    } catch {
      replaceState(null)
    } finally {
      loading.value = false
    }
  }

  async function save(nextConfig: EnterpriseBalanceAlertConfig) {
    const response = await AccountServer.saveEnterpriseBalanceAlert(normalizeEnterpriseBalanceAlertConfig(nextConfig))
    if (response.code === NetCode.SUCCESS && response.data) replaceState(response.data)
    return response
  }

  async function dismiss(alert: EnterpriseBalanceAlertPoolSummary) {
    const response = await AccountServer.dismissEnterpriseBalanceAlert({
      pool: normalizeEnterpriseBalanceAlertPool(alert.pool),
      cycleId: alert.cycleId,
    })
    if (response.code === NetCode.SUCCESS && response.data) replaceState(response.data)
    else await refresh(true)
    return response
  }

  function openDialog() {
    dialogVisible.value = true
    void refresh(true)
  }

  function closeDialog() {
    dialogVisible.value = false
  }

  return {
    state,
    loading,
    config,
    canManage,
    planMode,
    poolOptions,
    visibleAlerts,
    dialogVisible,
    refresh,
    save,
    dismiss,
    openDialog,
    closeDialog,
  }
}
